import React, { useState, useEffect } from 'react';
import PopupModal from 'components/PopupModal/PopupModal';

import PageTitle from 'shared/components/PageTitle/PageTitle';
import { connect, useSelector } from 'react-redux';
import { formatDate } from 'utils/date';
import { GetCarInsuranceAdvertHeroCard, GetVoucherDetails } from 'services/MyRacService';
import trackEvent from 'services/AnalyticsService';
import VoucherBanner from 'components/VoucherBanner/VoucherBanner';
import { useApplicationState } from '../../../../../hooks/applicationState-Context';
import InsuranceHeroCard from '../Components/InsuranceHeroCard';
import InsuranceSummaryCard from '../Components/InsuranceSummaryCard';
import styles from './CarInsurance.module.scss';

type Props = {
  carInsuranceState: MyRACStore.ProductsReduxState['carInsurance'];
};

const CarInsuranceScreen = ({ carInsuranceState }: Props) => {
  const { policies, hasError } = carInsuranceState;
  const featureFlags = useSelector((state: MyRACStore.Store) => state.featureToggle);
  const enableCarInsuranceVoucher = !!(featureFlags?.isLDReady && featureFlags?.enableCarInsuranceVoucher);
  const { appState, setPageLoading } = useApplicationState();
  const [showModal, setShowModal] = useState({ title: '', body: '' });
  const [heroCard, setHeroCard] = useState<WebExperienceApiContract.Models.HeroCard | undefined>(undefined);
  const [voucher, setVoucher] = useState<WebExperienceApiContract.Models.Voucher | undefined>(undefined);

  useEffect(() => {
    (async () => {
      try {
        const heroCardData = await GetCarInsuranceAdvertHeroCard()
        const voucherData = enableCarInsuranceVoucher ? await GetVoucherDetails() : undefined;

        if (heroCardData) setHeroCard(heroCardData);

        if (voucherData) {
          const carInsuranceVoucher = voucherData.find(
            (item: { voucherType: string }) => item.voucherType === 'CarInsuranceServiceVoucher',
          );
          setVoucher(carInsuranceVoucher);
        }
      } finally {
        setPageLoading(false, null);
      }
    })();
  }, [enableCarInsuranceVoucher]);

  if (hasError) return <div className="d-flex justify-content-center">An error occured while fetching your insurance policy.</div>;

  const closeModal = () => {
    setShowModal({ title: '', body: '' });
  };

  const setIndicativeImageModal = () => {
    const modalData = {
      title: 'About your car image',
      body:
        "Can't see your car, or not quite an exact match? We're only just getting started! Keep watching as we continue to add more and more cars to our already huge database.",
    };
    setShowModal(modalData);
  };

  const setAutoUpdateContentModal = (date: string) => {
    const modalData = {
      title: 'About the insurance details displayed',
      body: `We last ran an auto-update with the insurance provider on <strong>${formatDate(date, 'd/MM')} at ${formatDate(
        date,
        'HH:mm',
      )}</strong>. What's displayed in myRAC can take 24-48 hours to update any changes. In the mean time you can check details and or view changes in the Manage your policy section`,
    };

    setShowModal(modalData);
    return null;
  };

  const sortedPolicies = policies?.sort((a, b) => INSURANCE_STATUS_HIERACHY[a.status] - INSURANCE_STATUS_HIERACHY[b.status]);

  if (appState?.loading.status) return null;

  const clickHandler = () => {
    if (heroCard && heroCard.externalUrl) {
      trackEvent('MyRAC_CarInsurance_Hero_Card');
      window.open(heroCard.externalUrl, '_blank', 'noopener,noreferrer');
    }
  };
  const shouldShowVoucherBanner = enableCarInsuranceVoucher && voucher && !voucher.isExpired;
  return (
    <>
      {shouldShowVoucherBanner ? (
        <div className={styles.bannerContainer}>
          <div className={styles.voucherBanner}>
            <VoucherBanner voucher={voucher} />
          </div>
        </div>
        ) : null}
      <div style={{ backgroundColor: '#f1f1f1' }}>
        <div className={styles.container}>
          <div className="row">
            <div className="col-12 pt-3">
              <PageTitle text="Your Cover" />
            </div>
          </div>
        </div>
        <div className={styles.container}>
          <div className="row">
            <div className="col-md-8 ps-3">
              {sortedPolicies.map((data) => (
                <InsuranceSummaryCard
                  key={data.policyNumber}
                  setIndicativeImageModal={setIndicativeImageModal}
                  setAutoUpdateContentModal={setAutoUpdateContentModal}
                  details={data}
                />
              ))}
            </div>
            <div className="col-md-4">
              {heroCard ? (
                <InsuranceHeroCard heroCard={heroCard} onClick={clickHandler} />
              ) : null}
            </div>
          </div>
        </div>
        {showModal.title && showModal.body && (
          <PopupModal
            resultText={showModal.body}
            action={closeModal}
            resultTitle={showModal.title}
            innerCloseButton
            modalOk="Close"
            alignBodyCenter
            modalType="cancel"
            parseHtml
          />
        )}
      </div>
    </>
  );
};

const INSURANCE_STATUS_HIERACHY = {
  renewal: 1,
  active: 2,
  expired: 3,
  cancelled: 4,
};

const mapStateToProps = (state: MyRACStore.Store) => ({
  carInsuranceState: state.products.carInsurance,
});

export default connect(mapStateToProps)(CarInsuranceScreen);
